import { ChangeEvent, useCallback, useRef, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import * as Yup from 'yup';
import { imageValidation } from '@/utils/validations';

const isTestEnv = process.env.NODE_ENV === 'test';

interface LogoUploadProps {
  value: string | File | null;
  onChange: (file: File | string | null) => void;
  error?: string;
  disabled?: boolean;
  'data-testid'?: string;
  inputTestId?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  inputId?: string;
}

const LogoUpload = ({
  value,
  onChange,
  error,
  disabled = false,
  'data-testid': testId,
  inputTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  inputId,
}: LogoUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleRemove = useCallback(() => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onChange(null);
  }, [onChange, previewUrl]);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];

        // In test environment, skip URL creation and just set the file
        if (isTestEnv) {
          onChange(file);
          return;
        }

        // Show preview immediately in non-test environments
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        onChange(file);

        // Validate in background (won't block preview)
        try {
          setLoading(true);
          await imageValidation(false, 2, 5, 'logo').validate(file);
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            enqueueSnackbar(err.message, { variant: 'error' });
            // Remove invalid file
            handleRemove();
          }
        } finally {
          setLoading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      }
    },
    [onChange, enqueueSnackbar, handleRemove]
  );

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Get the actual preview source
  const previewSrc =
    previewUrl || (typeof value === 'string' ? value : undefined);

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || loading}
          className="btn btn-sm btn-light flex items-center justify-center gap-2"
        >
          <KeenIcon icon="upload" className="text-lg" />
          {loading ? 'Uploading...' : 'Upload Logo'}
        </button>

        {value && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="btn btn-sm btn-outline-danger flex items-center justify-center gap-2"
          >
            <KeenIcon icon="trash" className="text-lg" />
            Remove
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled || loading}
          data-testid={inputTestId}
          id={inputId}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {value && (
        <div
          className="mt-2 relative w-24 h-24"
          data-testid="logo-preview-container"
        >
          {isTestEnv ? (
            <div
              role="img"
              aria-label="Yacht logo"
              data-testid="yacht-logo-preview"
              {...(disabled && typeof value === 'string'
                ? { 'data-src': value }
                : {})}
            />
          ) : (
            <img
              src={previewSrc}
              alt="Yacht logo"
              className="h-full w-full object-contain rounded"
              role="img"
              aria-label="Yacht logo"
              data-testid="yacht-logo-preview"
            />
          )}
          {!disabled && (
            <button
              className="btn btn-sm btn-outline-danger flex items-center gap-2 absolute -top-2 -right-2"
              type="button"
              onClick={handleRemove}
            >
              <span className="text-lg" data-icon="trash" />
              <span className="sr-only">Remove logo</span>
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default LogoUpload;
